import React, { useCallback, useContext, useMemo, useState } from 'react';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';
import { Row, Select, Typography } from 'antd';
import useSwr from '@hooks/useSwr';
import ActionTypes from '@utils/deviceActions';
import { useTranslation } from 'react-i18next';
import SessionContext from '@providers/Session/SessionContext';
import SelectCustomers from '@components/SelectCustomers';
import { replaceUnderscoresAndCapitalize } from '@utils/formatters';
import { IContract, IDeviceActionRequestPayload } from '../types';
import useActivationUploadStep from './Activation/ActivationUploadStep';

interface IActionActivation {
  actionKey: number;
  uploadedDevicesState: any;
  currentStep: number;
  handleDeviceActions: (item: IDeviceActionRequestPayload) => void;
  manualUploadDevices: any;
}

const { Paragraph } = Typography;

const useActivation = ({
  actionKey,
  uploadedDevicesState,
  currentStep,
  handleDeviceActions,
  manualUploadDevices,
}: IActionActivation) => {
  const { selectedApp } = useContext(SessionContext);
  const { keycloak } = useContext(KeycloakContext);
  const { t: translate } = useTranslation(['device-actions-modal']);

  const loggedRoot = useMemo(() => {
    return keycloak.idTokenParsed?.customerType === 'ROOT';
  }, [keycloak]);

  const [fileOrManualUpload, setfileOrManualUpload] = useState<number>(0); // 0 file , 1 manual
  const [stringManualUpload, setStringManualUpload] = useState('');
  const [manualUpload, setManualUpload] = manualUploadDevices;
  const [uploadedDevices, setUploadedDevices] = uploadedDevicesState;

  const [selectedCustomer, setSelectedCustomer] = useState<
    number | string | undefined
  >();
  const [selectedContract, setSelectedContract] = useState<
    string | undefined
  >();

  const contractsMustFetch = useMemo(() => {
    if (actionKey === ActionTypes.ACTIVATION_MULTI_CONNECT) {
      if (!loggedRoot) {
        return {
          params: {
            customerId: keycloak?.idTokenParsed?.customerId,
            product: selectedApp?.toUpperCase(),
          },
        };
      }
      if (loggedRoot && selectedCustomer) {
        return {
          params: {
            customerId: selectedCustomer,
            product: selectedApp?.toUpperCase(),
          },
        };
      }
    }

    return undefined;
  }, [
    actionKey,
    keycloak.idTokenParsed,
    loggedRoot,
    selectedCustomer,
    selectedApp,
  ]);

  const { data: dataContracts, error: errorContracts } = useSwr<IContract[]>(
    contractsMustFetch ? '/service-proxy/contracts' : null,
    contractsMustFetch,
  );

  const contractOptions = useMemo(
    () =>
      Array.isArray(dataContracts)
        ? dataContracts?.map(({ acronym, assetNumber, nickname }) => ({
            value: assetNumber,
            label: `${assetNumber} ${
              nickname ? ` - ${nickname}` : ''
            }  - ${replaceUnderscoresAndCapitalize(acronym ?? '')}`,
          }))
        : [],
    [dataContracts],
  );

  const { step: activationUploadStep, uploadReinitState } =
    useActivationUploadStep({
      selectedAcronym: 'MULTICONNECT',
      translate,
      fileOrManualUploadState: [fileOrManualUpload, setfileOrManualUpload],
      stringManualUploadState: [stringManualUpload, setStringManualUpload],
      actionKey,
      setUploadedDevices,
      setManualUpload,
    });

  const contentSteps = useMemo(() => {
    // ROOT ONLY STEP
    const clientSelectionStep = {
      title: translate('steps.client.name'),
      content: (
        <div className="action-activation">
          <Paragraph>{`${translate('steps.client.description')}:`}</Paragraph>
          <Row align="middle" justify="start">
            <SelectCustomers
              value={selectedCustomer && Number(selectedCustomer)}
              onChange={value => setSelectedCustomer(value)}
            />
          </Row>
        </div>
      ),
    };

    // OTHERS THAN ROOT
    const everyoneStep = {
      title: translate('steps.contract.name'),
      content: (
        <div className="action-activation">
          <Paragraph>{`${translate('steps.contract.description')}:`}</Paragraph>
          <Row align="middle" justify="start">
            {errorContracts ? (
              <p>{translate('not-found-contract')}</p>
            ) : (
              <Select
                loading={!contractOptions}
                optionFilterProp="label"
                options={contractOptions}
                value={selectedContract}
                allowClear
                showSearch
                onChange={value => {
                  setSelectedContract(value);
                }}
              />
            )}
          </Row>
        </div>
      ),
    };

    if (loggedRoot) {
      return [clientSelectionStep, everyoneStep, activationUploadStep];
    }
    return [everyoneStep, activationUploadStep];
  }, [
    contractOptions,
    loggedRoot,
    selectedContract,
    selectedCustomer,
    translate,
    errorContracts,
    activationUploadStep,
  ]);

  const reinitState = () => {
    uploadReinitState();
    setSelectedContract(undefined);
    setSelectedCustomer(undefined);
    setfileOrManualUpload(0);
    setStringManualUpload('');
    setManualUpload([]);
  };

  const nextStepIsDisabled = useCallback(() => {
    if (loggedRoot) {
      if (currentStep === 0) {
        return !selectedCustomer;
      }
      if (currentStep === 1) {
        return !selectedContract;
      }

      if (currentStep === 2) {
        if (fileOrManualUpload === 0) {
          if (
            !uploadedDevices ||
            (uploadedDevices && uploadedDevices?.length === 0)
          )
            return true;
        } else if (stringManualUpload.length === 0) return true;
      }

      return false;
    }
    if (currentStep === 0) {
      return !selectedContract;
    }

    if (currentStep === 1) {
      if (fileOrManualUpload === 0) {
        if (
          !uploadedDevices ||
          (uploadedDevices && uploadedDevices?.length === 0)
        )
          return true;
      } else if (stringManualUpload.length === 0) return true;
    }

    return false;
  }, [
    uploadedDevices,
    selectedCustomer,
    selectedContract,
    currentStep,
    loggedRoot,
    stringManualUpload.length,
    fileOrManualUpload,
  ]);

  const onSubmit = useCallback(async () => {
    const payload: IDeviceActionRequestPayload = {};
    const devicesToSend = uploadedDevices;
    payload.contractId = selectedContract;
    payload.customer = selectedCustomer; // is a id
    payload.action = actionKey;

    if (fileOrManualUpload === 0) {
      payload.devices = devicesToSend || [];
    } else if (fileOrManualUpload === 1) {
      payload.devices = manualUpload.map((item: string) => {
        return {
          iccid: item,
        };
      });
    }

    return handleDeviceActions(payload);
  }, [
    selectedContract,
    selectedCustomer,
    uploadedDevices,
    handleDeviceActions,
    actionKey,
    manualUpload,
    fileOrManualUpload,
  ]);

  const activationData = {
    contentSteps,
    onSubmit,
    reinitState,
    nextStepIsDisabled,
  };

  return { activationData };
};

export default useActivation;
